// pages/sections/clientContactDemoData.js
//
// Tour-only sample data for the Client Contact Data section. Shown ONLY while
// the spotlight tour runs (gated by lib/tourDemo.js) so every step always has
// an ideal example to point at, regardless of the viewer's real book. Same
// nerd-reference cast as the Client Grouping tour (a nerd notices the
// franchises). Matches the /api/diClientContact response shape consumed by
// ClientContactSection → ClientContactCompareCards.
//
// Archivable: delete this file + revert the ClientContactSection demo edits.

const VIEWER = 'demo.partner@bmss.com';
const REVIEWED = { reviewedAt: '2026-06-30T15:00:00.000Z', reviewedBy: 'Demo Manager' };

// Build a comparable-field entry: PE is the master; each listed vendor value
// that differs from PE is flagged as a mismatch (drives the yellow cells).
const field = (peVal, others = {}) => {
  const mismatchBy = {};
  for (const [vendor, val] of Object.entries(others)) {
    if (val !== undefined && val !== null && val !== peVal) mismatchBy[vendor] = true;
  }
  return { pe: peVal, resolvedSource: null, mismatch: Object.keys(mismatchBy).length > 0, mismatchBy };
};

let _ci = 5000;
const row = (spec) => {
  const { fields, ...rest } = spec;
  const mismatchCount = Object.values(fields).filter(f => f.mismatch).length;
  return {
    contIndex: _ci++,
    managerEmail: VIEWER, partnerEmail: VIEWER,
    reviews: {}, createdRecord: null,
    isMFJ: false, spouseEmail: '', emailValid: true, spouseEmailValid: true, contactValid: true,
    mismatchCount,
    ...rest,
    fields,
  };
};

const ROWS = [
  // Address disagreement — Knuula still has the old Scranton address (The Office).
  row({
    clientCode: 'DEMO-DM01', clientName: 'Dunder Mifflin Paper Co.', clientOffice: 'BHM',
    vendors: {
      pe:       { address: '1725 Slough Ave', city: 'Scranton', state: 'PA', postCode: '18503', email: 'ap@dundermifflin.com', clientManager: 'Demo Manager', clientPartner: 'Demo Partner', phone: '(570) 555-0142' },
      knuula:   { address: '1200 Penn Ave',   city: 'Scranton', state: 'PA', zip: '18503',      email: 'ap@dundermifflin.com', manager: 'Demo Manager', partner: 'Demo Partner' },
      cch:      { address: '1725 Slough Ave', city: 'Scranton', state: 'PA', postCode: '18503', email: 'ap@dundermifflin.com', phone: '(570) 555-0142' },
      safesend: [{ email: 'ap@dundermifflin.com' }],
    },
    fields: {
      address:  field('1725 Slough Ave', { knuula: '1200 Penn Ave' }),
      city:     field('Scranton'),
      state:    field('PA'),
      postCode: field('18503'),
      email:    field('ap@dundermifflin.com'),
      phone:    field('(570) 555-0142'),
      manager:  field('Demo Manager'),
    },
  }),
  // Email disagreement — CCH + SafeSend carry a stale address (Far Cry 5).
  row({
    clientCode: 'DEMO-RA01', clientName: 'Rye & Sons Aviation', clientOffice: 'FHP',
    vendors: {
      pe:       { address: '88 Airstrip Rd', city: 'Fall’s End', state: 'MT', postCode: '59718', email: 'nick@ryeaviation.com', clientManager: 'Demo Manager', clientPartner: 'Demo Partner', phone: '(406) 555-0170' },
      knuula:   { address: '88 Airstrip Rd', city: 'Fall’s End', state: 'MT', zip: '59718',      email: 'nick@ryeaviation.com', manager: 'Demo Manager', partner: 'Demo Partner' },
      cch:      { address: '88 Airstrip Rd', city: 'Fall’s End', state: 'MT', postCode: '59718', email: 'nick.rye@gmail.com', phone: '(406) 555-0170' },
      safesend: [{ email: 'nick.rye@gmail.com' }, { email: 'kim@ryeaviation.com' }],
    },
    fields: {
      address:  field('88 Airstrip Rd'),
      city:     field('Fall’s End'),
      state:    field('MT'),
      postCode: field('59718'),
      email:    field('nick@ryeaviation.com', { cch: 'nick.rye@gmail.com', safesend: 'nick.rye@gmail.com' }),
      phone:    field('(406) 555-0170'),
      manager:  field('Demo Manager'),
    },
  }),
  // Everything agrees — already reviewed by the manager (Parks & Rec).
  row({
    clientCode: 'DEMO-LK01', clientName: 'Leslie B. Knope', clientOffice: 'FHP',
    reviews: { manager: REVIEWED },
    vendors: {
      pe:       { address: '123 Sullivan St', city: 'Pawnee', state: 'IN', postCode: '46001', email: 'leslie.knope@pawnee.gov', clientManager: 'Demo Manager', clientPartner: 'Demo Partner', phone: '(317) 555-0188' },
      knuula:   { address: '123 Sullivan St', city: 'Pawnee', state: 'IN', zip: '46001',      email: 'leslie.knope@pawnee.gov', manager: 'Demo Manager', partner: 'Demo Partner' },
      cch:      { address: '123 Sullivan St', city: 'Pawnee', state: 'IN', postCode: '46001', email: 'leslie.knope@pawnee.gov', phone: '(317) 555-0188' },
      safesend: [{ email: 'leslie.knope@pawnee.gov' }],
    },
    fields: {
      address:  field('123 Sullivan St'),
      city:     field('Pawnee'),
      state:    field('IN'),
      postCode: field('46001'),
      email:    field('leslie.knope@pawnee.gov'),
      phone:    field('(317) 555-0188'),
      manager:  field('Demo Manager'),
    },
  }),
  // Invalid PE email — blocks Mark Reviewed until fixed via Create New Record
  // (Breaking Bad).
  row({
    clientCode: 'DEMO-LP01', clientName: 'Los Pollos Hermanos, LLC', clientOffice: 'BHM',
    emailValid: false, contactValid: false,
    vendors: {
      pe:       { address: '4257 Isleta Blvd', city: 'Albuquerque', state: 'NM', postCode: '87105', email: 'gus@', clientManager: 'Demo Manager', clientPartner: 'Demo Partner', phone: '(505) 555-0111' },
      knuula:   { address: '4257 Isleta Blvd', city: 'Albuquerque', state: 'NM', zip: '87105',      email: 'gus@lospollos.com', manager: 'Demo Manager', partner: 'Demo Partner' },
      cch:      { address: '4257 Isleta Blvd', city: 'Albuquerque', state: 'NM', postCode: '87105', email: 'gus@lospollos.com', phone: '(505) 555-0111' },
      safesend: [{ email: 'gus@lospollos.com' }],
    },
    fields: {
      address:  field('4257 Isleta Blvd'),
      city:     field('Albuquerque'),
      state:    field('NM'),
      postCode: field('87105'),
      email:    field('gus@', { knuula: 'gus@lospollos.com', cch: 'gus@lospollos.com', safesend: 'gus@lospollos.com' }),
      phone:    field('(505) 555-0111'),
      manager:  field('Demo Manager'),
    },
  }),
  // Married filing jointly — spouse email required before it can be reviewed
  // (The Hobbit).
  row({
    clientCode: 'DEMO-TO01', clientName: 'Thorin & Dís Oakenshield', clientOffice: 'FHP',
    isMFJ: true, spouseEmail: '', spouseEmailValid: false, contactValid: false,
    vendors: {
      pe:       { address: '5 Under-the-Mountain', city: 'Erebor', state: 'CO', postCode: '80401', email: 'thorin@erebor.co', clientManager: 'Demo Manager', clientPartner: 'Demo Partner', phone: '(720) 555-0136' },
      knuula:   { address: '5 Under-the-Mountain', city: 'Erebor', state: 'CO', zip: '80401',      email: 'thorin@erebor.co', manager: 'Demo Manager', partner: 'Demo Partner' },
      cch:      { address: '5 Under-the-Mountain', city: 'Erebor', state: 'CO', postCode: '80401', email: 'thorin@erebor.co', phone: '(720) 555-0136' },
      safesend: [{ email: 'thorin@erebor.co' }],
    },
    fields: {
      address:  field('5 Under-the-Mountain'),
      city:     field('Erebor'),
      state:    field('CO'),
      postCode: field('80401'),
      email:    field('thorin@erebor.co'),
      phone:    field('(720) 555-0136'),
      manager:  field('Demo Manager'),
    },
  }),
];

export const CLIENT_CONTACT_DEMO = {
  rows: ROWS,
  compareFields: ['address', 'city', 'state', 'postCode', 'email', 'phone', 'manager'],
  officeWide: false,
};
